import { Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import { toNumber } from "../../../services/mappings";
import { renderNumberCell } from "../../../services/pdfService";
import { formatFechaISOLocal } from "../../../services/fechas";
import { enum_ENUM_FLUJO_EP, enum_ENUM_TIPO_RESPUESTA } from "../../../../../../../common/Dictionaries";

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    fontSize: 8,
  },
  row: {
    flexDirection: "row",
  },

  topRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },

  // ---- Tabla índices (izquierda) ----
  indicesTable: {
    width: "40%",
    border: "1pt solid #000",
  },
  idxHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#f2f2f2",
  },
  idxRow: {
    flexDirection: "row",
  },
  idxCell: {
    borderRight: "1pt solid #000",
    borderBottom: "1pt solid #000",
    paddingVertical: 3,
    paddingHorizontal: 3,
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
  },
  idxCellLast: {
    borderBottom: "1pt solid #000",
    paddingVertical: 3,
    paddingHorizontal: 3,
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
  },
  wIdx: { width: "20%" },
  wVal: { width: "20%" },

  // ---- Resumen (derecha) ----
  summaryWrap: {
    width: "27%",
  },
  summaryTable: {
    border: "1pt solid #000",
  },
  summaryRow: {
    flexDirection: "row",
    borderBottom: "1pt solid #000",
  },
  summaryLeft: {
    width: "74.5%",
    borderRight: "1pt solid #000",
    backgroundColor: "#00768a",
    color: "#fff",
    fontWeight: "bold",
    paddingVertical: 3,
    paddingHorizontal: 6,
    textAlign: "right",
  },
  summaryRight: {
    width: "25.5%",
    paddingVertical: 3,
    paddingHorizontal: 4,
    textAlign: "right",
    fontWeight: "bold",
  },
  summaryLeftLast: {
    width: "74.5%",
    borderRight: "1pt solid #000",
    backgroundColor: "#00768a",
    color: "#fff",
    fontWeight: "bold",
    paddingVertical: 3,
    paddingHorizontal: 6,
    textAlign: "right",
  },
  summaryRightLast: {
    width: "25.5%",
    paddingVertical: 3,
    paddingHorizontal: 4,
    textAlign: "right",
    fontWeight: "bold",
  },

  totalEpTable: {
    marginTop: 12,
    border: "1pt solid #000",
  },
  totalEpRow: {
    flexDirection: "row",
  },
  totalEpLeft: {
    width: "74.5%",
    borderRight: "1pt solid #000",
    backgroundColor: "#edad33",
    fontWeight: "bold",
    paddingVertical: 10,
    paddingHorizontal: 6,
    textAlign: "center",
  },
  totalEpRight: {
    width: "25.5%",
    paddingVertical: 10,
    paddingHorizontal: 4,
    textAlign: "right",
    fontWeight: "bold",
  },

  // ---- Aprobaciones (abajo, rojo) ----
  approvalsWrap: {
    marginTop: 16,
    width: "55%",
    marginLeft: "27%",
    border: "1pt solid #000",
  },
  approvalsTitle: {
    backgroundColor: "#e32636",
    color: "#fff",
    fontWeight: "bold",
    textAlign: "center",
    paddingVertical: 2,
    borderBottom: "1pt solid #000",
    fontSize: 11,
  },
  approvalsRoleRow: {
    flexDirection: "row",
    backgroundColor: "#e32636",
    borderBottom: "1pt solid #000",
  },
  approvalsRoleCell: {
    width: "50%",
    color: "#fff",
    fontWeight: "bold",
    textAlign: "center",
    paddingVertical: 2,
    borderRight: "1pt solid #000",
  },
  approvalsRoleCellLast: {
    width: "50%",
    color: "#fff",
    fontWeight: "bold",
    textAlign: "center",
    paddingVertical: 2,
  },
  approvalsBody: {
    flexDirection: "row",
  },
  roleCol: {
    width: "50%",
    borderRight: "1pt solid #000",
  },
  roleColLast: {
    width: "50%",
  },
  infoLine: {
    flexDirection: "row",
    borderBottom: "1pt solid #000",
    minHeight: 19,
  },
  infoLineLast: {
    flexDirection: "row",
    minHeight: 40,
  },
  labelCell: {
    width: "24%",
    borderRight: "1pt solid #000",
    paddingHorizontal: 3,
    paddingTop: 3,
    fontWeight: "bold",
  },
  valueCell: {
    width: "76%",
    paddingHorizontal: 3,
    paddingTop: 3,
  },
  firmaImageWrap: {
    paddingHorizontal: 2,
    paddingVertical: 2,
  },
  firmaImage: {
    maxWidth: "95%",
    maxHeight: 34,
    objectFit: "contain",
  },
  numberText: {
    fontSize: 8,
  },
  numberTextMillion: {
    fontSize: 7,
  },
  numberTextBillion: {
    fontSize: 6,
  },
  numberHundredMillion: {
    fontSize: 6,
  },
  numberTextStretch: {
    fontSize: 8,
  },
  numberTextMillionStretch: {
    fontSize: 5,
  },
  numberTextBillionStretch: {
    fontSize: 4,
  },
  numberHundredMillionStretch: {
    fontSize: 3,
  },
});

const normalizeSignatureSrc = (firma) => {
  if (!firma || typeof firma !== "string") return "";
  if (firma.startsWith("data:image")) return firma;
  return `data:image/png;base64,${firma}`;
};

const formatDateYMD = (value) => {
  if (!value) return "";
  if (typeof value === "string" && value.includes("T"))
    return value.split("T")[0];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const defaultIndices = [" ", " ", " ", " ", " "].map((name) => ({
  indice: name,
  valorBase: "",
  fechaValorBase: "",
  valorActual: "",
  fechaValorActual: "",
}));

const findStageByTriplet = (stages, tipo, flujo, agrupacion) => {
  const candidates = (stages ?? []).filter(
    (stage) =>
      Number(stage?.tipo_respuesta_id) === Number(tipo) &&
      Number(stage?.etapa_flujo_id) === Number(flujo) &&
      Number(stage?.agrupacion) === Number(agrupacion),
  );
  return (
    candidates.find((stage) => stage?.item_respuesta) ?? candidates[0] ?? null
  );
};

const buildRoleData = (headersData, tipo, flujo, agrupacion, displayName) => {
  const stage = findStageByTriplet(
    headersData?.l_etapas_revision,
    tipo,
    flujo,
    agrupacion,
  );
  const item = stage?.item_respuesta ?? {};
  return {
    nombre: item?.fullname ?? "",
    cargo: item?.cargo ?? "",
    // llega en UTC: el día se toma en la hora local (antes, de noche salía el día siguiente)
    fecha: formatFechaISOLocal(item?.fecha),
    firma: normalizeSignatureSrc(stage?.firma ?? item?.firma ?? ""),
    displayName: displayName,
  };
};

const flattenToLeafRows = (itemsList = []) =>
  itemsList.flatMap((item) => {
    const subs = item?.sub_partidas ?? [];
    return subs.length ? subs : [item];
  });

const mapPolinomiosToIndices = (polinomiosPayload) => {
  const source = Array.isArray(polinomiosPayload?.data)
    ? polinomiosPayload.data
    : Array.isArray(polinomiosPayload)
      ? polinomiosPayload
      : [];

  const seen = new Set();

  return source.reduce((acc, row) => {
    const indice = String(row?.indice ?? "").trim();
    if (!indice || seen.has(indice)) return acc;

    seen.add(indice);
    acc.push({
      indice,
      valorBase: row?.valoR_BASE_INDICE,
      fechaValorBase: formatDateYMD(row?.fechA_BASE),
      valorActual: row?.valoR_INDICE_REAJUSTE,
      fechaValorActual: formatDateYMD(row?.fechA_REAJUSTE),
    });

    return acc;
  }, []);
};

function DocumentFooter({
  headers,
  items,
  incrementos,
  indicesReajuste,
  polinomios,
}) {
  const data = headers?.data ?? headers ?? {};
  const itemRows = Array.isArray(items?.data)
    ? items.data
    : Array.isArray(items)
      ? items
      : [];
  const leafRows = flattenToLeafRows(itemRows);

  const moneda =
    data?.moneda === "CLP"
      ? "Pesos"
      : data?.moneda === "USD"
        ? "Dólares"
        : (data?.moneda ?? "");
  // Valores base desde subpartidas (o partidas sin hijos)
  const noAfectoReajuste = leafRows.reduce((acc, row) => {
    const base = toNumber(row?.detalle?.eP_MES_MONTO_BASE_MC);
    const reaj = toNumber(row?.detalle?.eP_MES_REAJUSTE_MC);
    return acc + (reaj === 0 ? base : 0);
  }, 0);

  const afectoReajuste = leafRows.reduce((acc, row) => {
    const base = toNumber(row?.detalle?.eP_MES_MONTO_BASE_MC);
    const reaj = toNumber(row?.detalle?.eP_MES_REAJUSTE_MC);
    return acc + (reaj !== 0 ? base : 0);
  }, 0);

  const reajuste = leafRows.reduce(
    (acc, row) => acc + toNumber(row?.detalle?.eP_MES_REAJUSTE_MC),
    0,
  );

  const increments = Array.isArray(incrementos?.data)
    ? incrementos.data
    : Array.isArray(incrementos)
      ? incrementos
      : [];

  // Descuentos = deducciones (tipo 1) del período actual ("2-")
  const descuentos = increments
    .filter((x) => x?.tipo === 1 && String(x?.iD_T ?? "").startsWith("1-"))
    .reduce((acc, x) => acc + toNumber(x?.monto), 0);

  const increment = increments
    .filter((x) => x?.tipo === 2 && String(x?.iD_T ?? "").startsWith("2-"))
    .reduce((acc, x) => acc + toNumber(x?.monto), 0);

  const totalEstadoPago =
    noAfectoReajuste + afectoReajuste + reajuste + increment - descuentos;

  const indicesFromPolinomios = mapPolinomiosToIndices(polinomios);

  const indices =
    indicesFromPolinomios.length > 0
      ? indicesFromPolinomios
      : Array.isArray(indicesReajuste) && indicesReajuste.length
        ? indicesReajuste
        : Array.isArray(data?.indicesReajuste) && data.indicesReajuste.length
          ? data.indicesReajuste
          : defaultIndices;
  // -> AEC (tipo_respuesta_id=1, etapa_flujo_id=1, agrupacion=1)
  // -> ADM (tipo_respuesta_id=4, etapa_flujo_id=5, agrupacion=1)
  //Los números de al final "1", previo a "EECC" y "Compañia" son de agrupación.
  const aec = buildRoleData(data, enum_ENUM_TIPO_RESPUESTA.AEC, enum_ENUM_FLUJO_EP.PREPARACION, 1, "EECC");
  const adm = buildRoleData(data, enum_ENUM_TIPO_RESPUESTA.ADN, enum_ENUM_FLUJO_EP.ABROPACION_ADN, 1, "Compañía");

  const renderRoleColumn = (role) => (
    <>
      <View style={styles.infoLine}>
        <Text style={styles.labelCell}>Nombre</Text>
        <Text style={styles.valueCell}>{role?.nombre ?? ""}</Text>
      </View>
      <View style={styles.infoLine}>
        <Text style={styles.labelCell}>Cargo</Text>
        <Text style={styles.valueCell}>{role?.cargo ?? ""}</Text>
      </View>
      <View style={styles.infoLine}>
        <Text style={styles.labelCell}>Fecha</Text>
        <Text style={styles.valueCell}>{role?.fecha ?? ""}</Text>
      </View>
      <View style={styles.infoLineLast}>
        <Text style={styles.labelCell}>Firma</Text>
        <View style={styles.valueCell}>
          {role?.firma ? (
            <View style={styles.firmaImageWrap}>
              <Image style={styles.firmaImage} src={role.firma} />
            </View>
          ) : null}
        </View>
      </View>
    </>
  );

  return (
    <View style={styles.container} wrap={false}>
      <View style={styles.topRow}>
        {/* IZQUIERDA: índices */}
        <View style={styles.indicesTable}>
          <View style={styles.idxHeaderRow}>
            <Text style={[styles.idxCell, styles.wIdx]}>Índice</Text>
            <Text style={[styles.idxCell, styles.wVal]}>Valor Base</Text>
            <Text
              hyphenationCallback={(word) => [word]}
              style={[styles.idxCell, styles.wVal]}
            >
              Fecha Valor Base
            </Text>
            <Text
              hyphenationCallback={(word) => [word]}
              style={[styles.idxCell, styles.wVal]}
            >
              Valor Actual
            </Text>
            <Text
              hyphenationCallback={(word) => [word]}
              style={[styles.idxCellLast, styles.wVal]}
            >
              Fecha Valor Actual
            </Text>
          </View>

          {indices.slice(0, 5).map((row, idx) => (
            <View style={styles.idxRow} key={`idx-${idx}`}>
              <Text style={[styles.idxCell, styles.wIdx]}>
                {row?.indice ?? ""}
              </Text>
              {renderNumberCell(
                styles,
                row?.valorBase ?? 0,
                [styles.idxCell, styles.wVal],
                null,
                false,
                2,
                2,
                moneda !== "UF",
              )}
              <Text style={[styles.idxCell, styles.wVal]}>
                {row?.fechaValorBase ?? ""}
              </Text>

              {renderNumberCell(
                styles,
                row?.valorActual ?? 0,
                [styles.idxCell, styles.wVal],
                null,
                false,
                2,
                2,
                moneda !== "UF",
              )}
              <Text style={[styles.idxCellLast, styles.wVal]}>
                {row?.fechaValorActual ?? ""}
              </Text>
            </View>
          ))}
        </View>
        <View style={{ width: "21%" }} />
        {/* Espacio entre columnas */}
        {/* DERECHA: resumen */}
        <View style={styles.summaryWrap}>
          <View style={styles.summaryTable}>
            <View
              style={[styles.summaryRow, { borderBottom: "1pt solid #000" }]}
            >
              <Text style={styles.summaryLeft}>Total No afecto a reajuste</Text>
              {renderNumberCell(
                styles,
                noAfectoReajuste,
                [],
                styles.summaryRight,
                false,
                2,
                2,
                moneda !== "UF",
              )}
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLeft}>Total afecto a reajuste</Text>
              {renderNumberCell(
                styles,
                afectoReajuste,
                [],
                styles.summaryRight,
                false,
                2,
                2,
                moneda !== "UF",
              )}
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLeft}>Reajuste</Text>
              {renderNumberCell(
                styles,
                reajuste,
                [],
                styles.summaryRight,
                false,
                2,
                2,
                moneda !== "UF",
              )}
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLeftLast}>Descuentos</Text>
              {renderNumberCell(
                styles,
                descuentos,
                [],
                styles.summaryRight,
                false,
                2,
                2,
                moneda !== "UF",
              )}
            </View>
          </View>

          <View style={styles.totalEpTable}>
            <View style={styles.totalEpRow}>
              <Text style={styles.totalEpLeft}>
                TOTAL ESTADO DE PAGO ({moneda})
              </Text>
              {renderNumberCell(
                styles,
                totalEstadoPago,
                [],
                styles.totalEpRight,
                false,
                2,
                2,
                moneda !== "UF",
              )}
            </View>
          </View>
        </View>
      </View>

      {/* ABAJO: aprobaciones */}
      <View style={styles.approvalsWrap}>
        <Text style={styles.approvalsTitle}>Administradores de Contrato</Text>

        <View style={styles.approvalsRoleRow}>
          <Text style={styles.approvalsRoleCell}>
            {adm.displayName || "Compañía"}
          </Text>
          <Text style={styles.approvalsRoleCellLast}>
            {aec.displayName || "EECC"}
          </Text>
        </View>

        <View style={styles.approvalsBody}>
          <View style={styles.roleCol}>{renderRoleColumn(adm)}</View>
          <View style={styles.roleColLast}>{renderRoleColumn(aec)}</View>
        </View>
      </View>
    </View>
  );
}

export default DocumentFooter;
